import { AxiosError } from 'axios';
import type { AdminSection } from '@/components/layout/admin/Sidebar';
import { ALL_ADMIN_SECTIONS, canAccessSection, sectionsForRole } from '@/pages/admin/demo/permissions/adminDemoPermissions';
import { createDemoSeed, type AuditEntry, type DemoAssignment, type DemoBookOrder, type DemoCoupon, type DemoInstructor, type DemoOrder, type DemoPayout, type DemoStore, type InstructorPaymentMethods } from '@/pages/admin/demo/seed/adminDemoSeed';
import { getAdminDemoActor, getAdminDemoRole } from '@/pages/admin/demo/role/adminDemoRole';

const STORAGE_KEY = 'maktech_admin_demo_v35';

type QbankQuestion = {
  id: string;
  prompt: string;
  options: string[];
  answerIndex: number;
  explanation: string;
  isPlaceholder: boolean;
};

function normalizeOrders(orders: DemoStore['orders']): DemoStore['orders'] {
  const normalized = (orders?.orders ?? []).map((raw) => {
    const order = raw as DemoOrder & { items: string[] | DemoOrder['items'] };
    const items = Array.isArray(order.items)
      ? order.items.map((item) =>
          typeof item === 'string' ? { title: item, price: 0 } : item,
        )
      : [];
    const email = String(order.email ?? '');
    const nameFromEmail = email.split('@')[0]?.replace(/[._]/g, ' ') ?? 'Customer';
    return {
      orderId: order.orderId,
      customerName: order.customerName || nameFromEmail,
      email,
      items,
      total: Number(order.total) || 0,
      method: order.method || '—',
      status: order.status || 'paid',
      createdAt: order.createdAt || order.paidAt || new Date().toISOString(),
      paidAt: order.paidAt || order.createdAt || new Date().toISOString(),
      txnRef: order.txnRef,
      refundRequest: order.refundRequest ?? null,
      refundedAt: order.refundedAt,
      refundDeniedAt: order.refundDeniedAt,
    } satisfies DemoOrder;
  });
  return { orders: normalized };
}

function normalizeBookOrders(orders: DemoStore['bookorders']): DemoStore['bookorders'] {
  const normalized = (orders?.orders ?? []).map((raw) => {
    const order = raw as DemoBookOrder & {
      items: { title: string; qty: number; unitPrice?: number }[];
      subtotal?: number;
      deliveryFee?: number;
      email?: string;
      city?: string;
      district?: string;
      paymentMethod?: string;
      orderedAt?: string;
    };
    const items = (order.items ?? []).map((item) => ({
      title: item.title,
      qty: item.qty,
      unitPrice: item.unitPrice ?? Math.round((order.total || 0) / Math.max(1, item.qty)),
    }));
    const subtotal = order.subtotal ?? items.reduce((sum, item) => sum + item.unitPrice * item.qty, 0);
    const deliveryFee = order.deliveryFee ?? Math.max(0, (order.total || 0) - subtotal);
    const address = String(order.address ?? '');
    const [city = '—', district = city] = address.includes(',')
      ? address.split(',').map((part) => part.trim())
      : [address, address];
    return {
      orderId: order.orderId,
      name: order.name,
      email: order.email || `${order.name.toLowerCase().replace(/\s+/g, '.')}@demo.com`,
      phone: order.phone,
      address: address.includes(',') ? address.split(',')[0]!.trim() : address,
      city: order.city || city,
      district: order.district || district,
      items,
      subtotal,
      deliveryFee,
      total: order.total,
      paymentMethod: order.paymentMethod || 'Cash on delivery',
      status: order.status || 'pending',
      notes: order.notes,
      orderedAt: order.orderedAt || new Date().toISOString(),
      shippedAt: order.shippedAt,
      deliveredAt: order.deliveredAt,
    } satisfies DemoBookOrder;
  });
  const next = { orders: normalized, counts: orders?.counts ?? {} };
  recalcBookOrderCounts(next);
  return next;
}

function recalcBookOrderCounts(bookorders: DemoStore['bookorders']): void {
  const orders = bookorders.orders;
  bookorders.counts = {
    processing: orders.filter((o) => o.status === 'pending' || o.status === 'processing').length,
    packed: orders.filter((o) => o.status === 'packed').length,
    shipped: orders.filter((o) => o.status === 'shipped').length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
  };
}

function normalizePayouts(payouts: DemoStore['payouts']): DemoStore['payouts'] {
  const normalized = (payouts?.payouts ?? []).map((raw) => {
    const payout = raw as DemoPayout & { paid?: boolean };
    const status = payout.status ?? (payout.paid ? 'paid' : 'pending');
    const name = payout.instructor || 'Instructor';
    const slug = name.toLowerCase().replace(/\s+/g, '.');
    return {
      instructorId: payout.instructorId,
      instructor: name,
      email: payout.email || `${slug}@maktechacademy.com`,
      period: payout.period || 'Jul 2026',
      gross: payout.gross,
      platform: payout.platform,
      instructorShare: payout.instructorShare,
      status,
      paymentMethod: payout.paymentMethod || 'bKash',
      payoutAccount: payout.payoutAccount || '—',
      courses: payout.courses?.length
        ? payout.courses
        : [{ title: 'Course sales', sales: payout.gross, share: payout.instructorShare }],
      paidAt: payout.paidAt,
      reference: payout.reference,
    } satisfies DemoPayout;
  });
  return { commissionPct: payouts?.commissionPct ?? 30, payouts: normalized };
}

function normalizeCoupons(coupons: DemoStore['coupons']): DemoStore['coupons'] {
  const normalized = (coupons?.coupons ?? []).map((raw) => {
    const coupon = raw as DemoCoupon & { pct?: number; audience?: DemoCoupon['audience'] };
    const discountType = coupon.discountType ?? 'percent';
    const value = coupon.value ?? coupon.pct ?? 0;
    return {
      code: coupon.code,
      discountType,
      value,
      courseSlug: coupon.courseSlug ?? null,
      courseTitle: coupon.courseTitle ?? null,
      audience: coupon.audience === 'affiliate' ? 'affiliate' : 'public',
      expiresAt: coupon.expiresAt ?? null,
      active: coupon.active ?? true,
      createdAt: coupon.createdAt || new Date().toISOString(),
    } satisfies DemoCoupon;
  });
  return { coupons: normalized };
}

function normalizeAssignments(assignments: DemoStore['assignments']): DemoStore['assignments'] {
  const list = (assignments?.assignments ?? []).map((raw) => {
    const a = raw as DemoAssignment & { brief?: string; instructionsHtml?: string };
    const brief = a.brief || '';
    return {
      id: a.id,
      title: a.title,
      brief,
      instructionsHtml: a.instructionsHtml || (brief ? `<p>${brief}</p>` : ''),
      course: a.course,
      courseSlug: a.courseSlug || '',
      dueDate: a.dueDate,
      submissions: a.submissions ?? 0,
      reviewed: a.reviewed ?? 0,
      createdAt: a.createdAt || new Date().toISOString(),
    } satisfies DemoAssignment;
  });
  return {
    reviewQueue:
      assignments?.reviewQueue ?? list.reduce((sum, a) => sum + Math.max(0, a.submissions - a.reviewed), 0),
    assignments: list,
  };
}

function normalizeStore(parsed: DemoStore): DemoStore {
  parsed.qbankQuestions = normalizeQbankQuestions(parsed.qbankQuestions);
  parsed.orders = normalizeOrders(parsed.orders);
  if (!parsed.books?.books?.length) {
    parsed.books = createDemoSeed().books;
  }
  parsed.bookorders = normalizeBookOrders(parsed.bookorders);
  parsed.payouts = normalizePayouts(parsed.payouts);
  parsed.coupons = normalizeCoupons(parsed.coupons);
  parsed.assignments = normalizeAssignments(parsed.assignments);
  if (!Array.isArray(parsed.affiliates?.sales)) {
    parsed.affiliates = { ...parsed.affiliates, sales: createDemoSeed().affiliates.sales };
  }
  return parsed;
}

function normalizeQbankQuestions(raw: DemoStore['qbankQuestions']): DemoStore['qbankQuestions'] {
  const next: DemoStore['qbankQuestions'] = {};
  for (const [slug, value] of Object.entries(raw ?? {})) {
    if (Array.isArray(value)) {
      next[slug] = { '0': value as QbankQuestion[] };
    } else if (value && typeof value === 'object') {
      next[slug] = value as DemoStore['qbankQuestions'][string];
    }
  }
  return next;
}

function getCourseModules(store: DemoStore, courseSlug: string): { id: string; title: string; order: number }[] {
  const details = store.courseDetails[courseSlug];
  const raw = details?.modules as { title: string; order?: number }[] | undefined;
  if (raw?.length) {
    return raw.map((m, i) => ({
      id: String(m.order ?? i),
      title: m.title || `Module ${i + 1}`,
      order: m.order ?? i,
    }));
  }
  return [
    { id: '0', title: 'Module 1 — Introduction', order: 0 },
    { id: '1', title: 'Module 2 — Core concepts', order: 1 },
    { id: '2', title: 'Module 3 — Practice & projects', order: 2 },
  ];
}

function countModuleQuestions(store: DemoStore, courseSlug: string, moduleId: string): number {
  return store.qbankQuestions[courseSlug]?.[moduleId]?.length ?? 0;
}

function countCourseQuestions(store: DemoStore, courseSlug: string): number {
  const byModule = store.qbankQuestions[courseSlug] ?? {};
  return Object.values(byModule).reduce((sum, list) => sum + list.length, 0);
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function readStore(): DemoStore {
  if (isBrowser()) {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as DemoStore;
        if (parsed && typeof parsed === 'object' && parsed.overview) {
          return normalizeStore(parsed);
        }
      } catch {
        /* fall through */
      }
    }
  }
  return createDemoSeed();
}

function writeStore(store: DemoStore): void {
  if (!isBrowser()) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function forbidden(): never {
  throw new AxiosError(
    'Forbidden',
    '403',
    undefined,
    undefined,
    {
      status: 403,
      statusText: 'Forbidden',
      headers: {},
      config: {} as never,
      data: { error: { code: 'FORBIDDEN', message: "You don't have access to this section." } },
    },
  );
}

function assertAccess(section: string, role: string): void {
  if (role === 'instructor' || role === 'mentor') {
    const store = readStore();
    const profile = resolveInstructorProfile(store);
    const includePayment = profile?.source === 'application' && profile.status === 'active' && !profile.suspended;
    if (!canAccessSection(role, section, { includePayment })) forbidden();
    return;
  }
  if (!canAccessSection(role, section)) forbidden();
}

type DemoAffiliateRow = DemoStore['affiliates']['affiliates'][number];

function resolveAffiliateProfile(store: DemoStore, emailOverride?: string): DemoAffiliateRow | null {
  const email = (emailOverride ?? getAdminDemoActor().email).toLowerCase();
  return (
    store.affiliates.affiliates.find(
      (a) => a.email.toLowerCase() === email && a.status === 'active',
    ) ?? null
  );
}

function normalizeAffiliatePaymentMethods(
  raw: DemoAffiliateRow['paymentMethods'] | null | undefined,
): InstructorPaymentMethods {
  const base = emptyPaymentMethods();
  if (!raw) return base;
  return {
    bank: raw.bank ?? null,
    bkash: raw.bkash ?? null,
    nagad: raw.nagad ?? null,
    rocket: 'rocket' in raw ? (raw.rocket ?? null) : null,
  };
}

function buildSavedAccounts(methods: InstructorPaymentMethods) {
  return {
    bank: methods.bank
      ? {
          label: 'Bank transfer',
          detail: `${methods.bank.bankName} · ${methods.bank.accountName} · ****${methods.bank.accountNumber.slice(-4)}`,
        }
      : null,
    bkash: methods.bkash ? { label: 'bKash', detail: methods.bkash } : null,
    nagad: methods.nagad ? { label: 'Nagad', detail: methods.nagad } : null,
    rocket: methods.rocket ? { label: 'Rocket', detail: methods.rocket } : null,
  };
}

function applyPaymentMethodsPatch(
  current: InstructorPaymentMethods,
  methods: Partial<InstructorPaymentMethods>,
): InstructorPaymentMethods {
  const next = { ...current };
  if ('bank' in methods) {
    const bank = methods.bank;
    next.bank =
      bank && bank.bankName && bank.accountName && bank.accountNumber
        ? {
            bankName: String(bank.bankName).trim(),
            accountName: String(bank.accountName).trim(),
            accountNumber: String(bank.accountNumber).trim(),
            routingNumber: String(bank.routingNumber ?? '').trim(),
          }
        : null;
  }
  if ('bkash' in methods) next.bkash = methods.bkash ? String(methods.bkash).trim() : null;
  if ('nagad' in methods) next.nagad = methods.nagad ? String(methods.nagad).trim() : null;
  if ('rocket' in methods) next.rocket = methods.rocket ? String(methods.rocket).trim() : null;
  return next;
}

function resolvePayoutAccount(
  methods: InstructorPaymentMethods,
  payMethod: 'bank' | 'bkash' | 'nagad' | 'rocket',
): { paymentMethod: string; payoutAccount: string } {
  if (payMethod === 'bank') {
    if (!methods.bank) throw new Error('Save a bank account first');
    return {
      paymentMethod: 'Bank transfer',
      payoutAccount: `${methods.bank.bankName} · ${methods.bank.accountNumber}`,
    };
  }
  if (payMethod === 'bkash') {
    if (!methods.bkash) throw new Error('Save a bKash number first');
    return { paymentMethod: 'bKash', payoutAccount: methods.bkash };
  }
  if (payMethod === 'nagad') {
    if (!methods.nagad) throw new Error('Save a Nagad number first');
    return { paymentMethod: 'Nagad', payoutAccount: methods.nagad };
  }
  if (payMethod === 'rocket') {
    if (!methods.rocket) throw new Error('Save a Rocket number first');
    return { paymentMethod: 'Rocket', payoutAccount: methods.rocket };
  }
  throw new Error('Choose a payout method');
}

function getOrderManagerOverviewData(store: DemoStore) {
  const courseOrders = store.orders.orders;
  const bookOrders = store.bookorders.orders;
  const refundRequests = courseOrders.filter((o) => o.status === 'refund_requested').length;
  const bookPending = bookOrders.filter((o) => o.status === 'pending' || o.status === 'processing').length;
  return {
    kpis: {
      courseOrders: courseOrders.length,
      refundRequests,
      bookOrders: bookOrders.length,
      bookPending,
    },
    recentCourseOrders: courseOrders.slice(0, 5).map((o) => ({
      orderId: o.orderId,
      email: o.email,
      total: o.total,
      status: o.status,
    })),
    recentBookOrders: bookOrders.slice(0, 5).map((o) => ({
      orderId: o.orderId,
      name: o.name,
      total: o.total,
      status: o.status,
    })),
  };
}

function getAffiliateOverviewData(store: DemoStore) {
  const profile = resolveAffiliateProfile(store);
  if (!profile) forbidden();
  const mine = store.affiliates.withdrawals.filter(
    (w) => w.email.toLowerCase() === profile.email.toLowerCase() || w.code === profile.code,
  );
  const pending = mine.filter((w) => w.status === 'pending');
  const paid = mine.filter((w) => w.status === 'paid');
  const pendingAmount = pending.reduce((s, w) => s + w.amount, 0);
  const paidAmount = paid.reduce((s, w) => s + w.amount, 0);
  const commissionPct = store.settings.settings.affiliateCommissionPct ?? 12;
  const minWithdrawal = 10000;
  const recentSales = (store.affiliates.sales ?? [])
    .filter((s) => s.affiliateId === profile.id || s.affiliateCode === profile.code)
    .slice(0, 5)
    .map((s) => ({
      id: s.id,
      courseTitle: s.courseTitle,
      commission: s.commission,
      couponCode: s.couponCode,
      createdAt: s.createdAt,
    }));
  return {
    profile: {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      code: profile.code,
      status: profile.status,
      joinedAt: profile.joinedAt,
    },
    kpis: {
      clicks: profile.clicks,
      signups: profile.signups,
      earned: profile.earned,
      available: profile.available,
      pending: pendingAmount,
      paid: paidAmount,
    },
    commissionPct,
    minWithdrawal,
    recentSales,
  };
}

function getAffiliateLinkData(store: DemoStore) {
  const overview = getAffiliateOverviewData(store);
  return {
    code: overview.profile.code,
    commissionPct: overview.commissionPct,
    linkPath: `/?ref=${overview.profile.code}`,
  };
}

function getAffiliateSalesData(store: DemoStore) {
  const profile = resolveAffiliateProfile(store);
  if (!profile) forbidden();
  const sales = (store.affiliates.sales ?? []).filter(
    (s) => s.affiliateId === profile.id || s.affiliateCode === profile.code,
  );
  const totalCommission = sales.reduce((sum, s) => sum + s.commission, 0);
  const totalOrderAmount = sales.reduce((sum, s) => sum + s.orderAmount, 0);
  return {
    profile: { id: profile.id, name: profile.name, code: profile.code },
    commissionPct: store.settings.settings.affiliateCommissionPct ?? 12,
    stats: {
      salesCount: sales.length,
      totalOrderAmount,
      totalCommission,
    },
    sales,
  };
}

function getAffiliateCoursesData(store: DemoStore) {
  const profile = resolveAffiliateProfile(store);
  if (!profile) forbidden();
  const commissionPct = store.settings.settings.affiliateCommissionPct ?? 12;
  const now = Date.now();
  const affiliatePromos = store.coupons.coupons.filter((c) => {
    if (c.audience !== 'affiliate' || !c.active) return false;
    if (c.expiresAt && new Date(`${c.expiresAt}T23:59:59`).getTime() < now) return false;
    return true;
  });
  const courses = store.courses.courses
    .filter((c) => !c.hidden)
    .map((c) => {
      const promos = affiliatePromos
        .filter((p) => !p.courseSlug || p.courseSlug === c.slug)
        .map((p) => ({
          code: p.code,
          discountType: p.discountType,
          value: p.value,
          discountLabel: p.discountType === 'flat' ? `−৳${Math.round(p.value / 100)}` : `−${p.value}%`,
          expiresAt: p.expiresAt,
          referralPath: `/course/${c.slug}?ref=${profile.code}&coupon=${p.code}`,
        }));
      return {
        slug: c.slug,
        title: c.title,
        category: c.category,
        price: c.price,
        instructorName: c.instructorName,
        referralPath: `/course/${c.slug}?ref=${profile.code}`,
        promos,
      };
    });
  return {
    code: profile.code,
    commissionPct,
    courses,
    /** Site-wide affiliate promos (all courses). */
    globalPromos: affiliatePromos
      .filter((p) => !p.courseSlug)
      .map((p) => ({
        code: p.code,
        discountLabel: p.discountType === 'flat' ? `−৳${Math.round(p.value / 100)}` : `−${p.value}%`,
        expiresAt: p.expiresAt,
        referralPath: `/?ref=${profile.code}&coupon=${p.code}`,
      })),
  };
}

function findAffiliateByCode(store: DemoStore, code: string): DemoAffiliateRow | null {
  const normalized = code.trim().toUpperCase();
  return (
    store.affiliates.affiliates.find(
      (a) => a.code.toUpperCase() === normalized && a.status === 'active',
    ) ?? null
  );
}

function methodLabelFromWithdrawal(method: string): string {
  if (method === 'bkash') return 'bKash';
  if (method === 'nagad') return 'Nagad';
  if (method === 'rocket') return 'Rocket';
  if (method === 'bank') return 'Bank transfer';
  return method;
}

function getAffiliatePaymentData(store: DemoStore) {
  const profile = resolveAffiliateProfile(store);
  if (!profile) forbidden();
  const methods = normalizeAffiliatePaymentMethods(profile.paymentMethods);
  const withdrawals = store.affiliates.withdrawals.filter(
    (w) => w.email.toLowerCase() === profile.email.toLowerCase() || w.code === profile.code,
  );
  const payouts: DemoPayout[] = withdrawals.map((w) => {
    const d = new Date(w.requestedAt);
    const period = Number.isNaN(d.getTime())
      ? 'Request'
      : `Request · ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
    return {
      instructorId: profile.id,
      instructor: profile.name,
      email: profile.email,
      period,
      gross: w.amount,
      platform: 0,
      instructorShare: w.amount,
      status: w.status === 'paid' ? 'paid' : 'pending',
      paymentMethod: methodLabelFromWithdrawal(w.method),
      payoutAccount: w.account,
      courses: [{ title: 'Affiliate commission', sales: w.amount, share: w.amount }],
    };
  });
  return {
    instructor: { id: profile.id, name: profile.name, email: profile.email },
    paymentMethods: methods,
    savedAccounts: buildSavedAccounts(methods),
    availableBalance: profile.available,
    payouts,
    commissionPct: store.settings.settings.affiliateCommissionPct ?? 12,
  };
}

function resolveInstructorProfile(store: DemoStore, emailOverride?: string): DemoInstructor | null {
  const email = (emailOverride ?? getAdminDemoActor().email).toLowerCase();
  return (
    store.instructors.instructors.find(
      (i) => i.email.toLowerCase() === email && i.status === 'active' && !i.suspended,
    ) ?? null
  );
}

function courseSlugsForInstructor(store: DemoStore, profile: DemoInstructor | null): string[] | null {
  if (!profile) return [];
  if (profile.courseAccess === 'all') return null; // null = all courses
  if (profile.courseAccess === 'selected' || profile.courseSlugs.length) {
    return profile.courseSlugs;
  }
  // own without explicit slugs: match by name / id on course rows
  return store.courses.courses
    .filter((c) => c.instructorId === profile.id || c.instructorName === profile.name)
    .map((c) => c.slug);
}

function filterByCourseSlugs<T>(items: T[], slugs: string[] | null, getSlug: (item: T) => string): T[] {
  if (slugs === null) return items;
  const allowed = new Set(slugs);
  return items.filter((item) => allowed.has(getSlug(item)));
}

function emptyPaymentMethods(): InstructorPaymentMethods {
  return { bank: null, bkash: null, nagad: null, rocket: null };
}

function buildInstructorOverview(store: DemoStore, slugs: string[] | null) {
  const reportCourses = filterByCourseSlugs(store.reports.courses, slugs, (c) => c.slug);
  const courseRows = filterByCourseSlugs(store.courses.courses, slugs, (c) => c.slug);
  const enrolments = filterByCourseSlugs(store.enrollments.enrollments, slugs, (e) => e.courseSlug);
  const titles = new Set(courseRows.map((c) => c.title));
  const certificates = store.certs.certificates.filter((c) => !c.revoked && titles.has(c.course));
  const revenue = reportCourses.reduce((sum, c) => sum + c.revenue, 0);
  const enrolled = reportCourses.reduce((sum, c) => sum + c.enrolled, 0);
  const completed = reportCourses.reduce((sum, c) => sum + c.completed, 0);
  const studentEmails = new Set(enrolments.map((e) => e.email.toLowerCase()));
  const students =
    studentEmails.size > 0 ? studentEmails.size : courseRows.reduce((s, c) => s + c.realStudents, 0);
  return {
    kpis: {
      courses: courseRows.length,
      revenue,
      students,
      enrolments: enrolled || enrolments.length,
      certificates: certificates.length,
      completionRate: enrolled ? Math.round((completed / enrolled) * 100) : 0,
    },
    revenueByCourse: reportCourses
      .map((c) => ({ title: c.title, revenue: c.revenue }))
      .sort((a, b) => b.revenue - a.revenue),
    recentOrders: [] as { orderId: string; email: string; total: number; status: string }[],
  };
}

function getStaffProfileData(store: DemoStore, role: string) {
  if (role === 'affiliate') {
    const profile = resolveAffiliateProfile(store);
    if (!profile) forbidden();
    return {
      kind: 'affiliate' as const,
      id: profile.id,
      name: profile.name,
      email: profile.email,
      phone: profile.phone ?? '',
      bio: profile.bio ?? '',
      headline: '',
      code: profile.code,
      status: profile.status,
      joinedAt: profile.joinedAt,
      source: null,
      courseAccess: null,
      courseTitles: [] as string[],
      topic: null as string | null,
    };
  }
  if (role === 'instructor' || role === 'mentor') {
    const profile = resolveInstructorProfile(store);
    if (!profile) forbidden();
    return {
      kind: 'instructor' as const,
      id: profile.id,
      name: profile.name,
      email: profile.email,
      phone: profile.phone ?? '',
      bio: profile.bio ?? '',
      headline: profile.headline ?? '',
      code: null as string | null,
      status: profile.status,
      joinedAt: profile.createdAt,
      source: profile.source,
      courseAccess: profile.courseAccess,
      courseTitles: profile.courseTitles ?? [],
      topic: profile.topic,
    };
  }
  forbidden();
}

function getPaymentData(store: DemoStore) {
  const profile = resolveInstructorProfile(store);
  if (!profile || profile.source !== 'application') forbidden();
  const methods = profile.paymentMethods ?? emptyPaymentMethods();
  const payouts = store.payouts.payouts.filter(
    (p) => p.instructorId === profile.id || p.email.toLowerCase() === profile.email.toLowerCase(),
  );
  return {
    instructor: { id: profile.id, name: profile.name, email: profile.email },
    paymentMethods: methods,
    savedAccounts: buildSavedAccounts(methods),
    availableBalance: profile.availableBalance ?? 0,
    payouts,
    commissionPct: store.payouts.commissionPct,
  };
}

function addAudit(store: DemoStore, action: string, meta: unknown = null): void {
  const actor = getAdminDemoActor();
  const entry: AuditEntry = {
    action,
    meta,
    actor: actor.email,
    role: actor.role,
    createdAt: new Date().toISOString(),
  };
  store.audit.entries.unshift(entry);
}

function randomPassword(length = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$';
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)]!;
  }
  if (out === 'welcome1') return randomPassword(length);
  return out;
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

function parseQs(qs: string): URLSearchParams {
  const raw = qs.startsWith('?') ? qs.slice(1) : qs;
  return new URLSearchParams(raw);
}

function getStudentsData(store: DemoStore) {
  const students = store.users.users
    .filter((u) => u.role === 'student')
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      suspended: u.suspended,
      points: u.points,
      joinedAt: u.joinedAt,
      enrolments: store.enrollments.enrollments.filter((e) => e.userId === u.id).length,
      certificates: store.certs.certificates.filter((c) => c.holder === u.name && !c.revoked).length,
    }));
  return { students };
}

function getOrderManagersData(store: DemoStore) {
  const managers = store.users.users
    .filter((u) => u.role === 'order_manager')
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      suspended: u.suspended,
      joinedAt: u.joinedAt,
    }));
  return { managers };
}

function getAffiliatePayoutsData(store: DemoStore) {
  const withdrawals = store.affiliates.withdrawals ?? [];
  const pending = withdrawals.filter((w) => w.status === 'pending');
  const paid = withdrawals.filter((w) => w.status === 'paid');
  return {
    stats: {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, w) => sum + w.amount, 0),
      paidCount: paid.length,
      paidAmount: paid.reduce((sum, w) => sum + w.amount, 0),
    },
    withdrawals,
  };
}

function getQbankData(store: DemoStore, qs: string, courseSlugs: string[] | null = null) {
  const params = parseQs(qs);
  const course = params.get('course');
  const moduleParam = params.get('module');
  const allCourses = store.courses.courses.map((c) => ({
    slug: c.slug,
    title: c.title,
    count: countCourseQuestions(store, c.slug),
  }));
  const courses = filterByCourseSlugs(allCourses, courseSlugs, (c) => c.slug);
  const selected = course && courses.some((c) => c.slug === course) ? course : (courses[0]?.slug ?? null);
  const modules = selected
    ? getCourseModules(store, selected).map((m) => ({
        ...m,
        count: countModuleQuestions(store, selected, m.id),
      }))
    : [];
  const selectedModule =
    moduleParam && modules.some((m) => m.id === moduleParam) ? moduleParam : (modules[0]?.id ?? null);
  const questions =
    selected && selectedModule ? (store.qbankQuestions[selected]?.[selectedModule] ?? []) : [];
  return { courses, selected, modules, selectedModule, questions };
}

function sectionData(store: DemoStore, section: string, qs: string, role: string): unknown {
  const isInstructor = role === 'instructor' || role === 'mentor';
  const profile = isInstructor ? resolveInstructorProfile(store) : null;
  const slugs = isInstructor ? courseSlugsForInstructor(store, profile) : null;

  switch (section) {
    case 'overview':
      if (role === 'affiliate') return getAffiliateOverviewData(store);
      if (role === 'order_manager') return getOrderManagerOverviewData(store);
      if (isInstructor) return buildInstructorOverview(store, slugs);
      return store.overview;
    case 'afflink':
      return getAffiliateLinkData(store);
    case 'affcourses':
      return getAffiliateCoursesData(store);
    case 'affsales':
      return getAffiliateSalesData(store);
    case 'profile':
      return getStaffProfileData(store, role);
    case 'reports':
      if (isInstructor) {
        return { courses: filterByCourseSlugs(store.reports.courses, slugs, (c) => c.slug) };
      }
      return store.reports;
    case 'courses':
      if (isInstructor) {
        return { courses: filterByCourseSlugs(store.courses.courses, slugs, (c) => c.slug) };
      }
      return store.courses;
    case 'bundles':
      return store.bundles;
    case 'qbankadm':
      return getQbankData(store, qs, isInstructor ? slugs : null);
    case 'enrollments':
      return store.enrollments;
    case 'students':
      return getStudentsData(store);
    case 'ordermanagers':
      return getOrderManagersData(store);
    case 'staff':
      return store.staff;
    case 'orders':
      return store.orders;
    case 'books':
      return store.books;
    case 'bookorders':
      return store.bookorders;
    case 'payouts':
      return store.payouts;
    case 'payment':
      if (role === 'affiliate') return getAffiliatePaymentData(store);
      return getPaymentData(store);
    case 'coupons':
      return store.coupons;
    case 'assignments':
      if (isInstructor) {
        return {
          assignments: filterByCourseSlugs(store.assignments.assignments, slugs, (a) => a.courseSlug),
        };
      }
      return store.assignments;
    case 'content':
      return store.content;
    case 'downloads':
      return store.downloads;
    case 'qa':
      return store.qa;
    case 'tickets':
      return store.tickets;
    case 'reviews':
      if (isInstructor) {
        return {
          reviews: filterByCourseSlugs(store.reviews.reviews, slugs, (r) => r.courseSlug),
        };
      }
      return store.reviews;
    case 'instructors':
      return store.instructors;
    case 'leads':
      return store.leads;
    case 'freeresources':
      return store.freeresources;
    case 'pdfleads':
      return store.pdfleads;
    case 'counselling':
      return store.counselling;
    case 'affiliates':
      return store.affiliates;
    case 'affiliatepayouts':
      return getAffiliatePayoutsData(store);
    case 'live':
      return store.live;
    case 'masterclass':
      return store.masterclass;
    case 'certs':
      return store.certs;
    case 'announce':
      return store.announce;
    case 'settings':
      return store.settings;
    case 'audit':
      return store.audit;
    default:
      forbidden();
  }
}

export function demoGetAdminSections(role: string, email?: string): { role: string; sections: AdminSection[] } {
  let includePayment = false;
  if (role === 'instructor' || role === 'mentor') {
    const profile = resolveInstructorProfile(readStore(), email);
    includePayment = !!profile && profile.source === 'application' && profile.status === 'active' && !profile.suspended;
  }
  const sections = sectionsForRole(role, { includePayment });
  if (!sections.length) forbidden();
  return { role, sections };
}

export async function demoGetSection<T>(section: string, qs: string, role?: string): Promise<T> {
  const r = role ?? getAdminDemoRole();
  assertAccess(section, r);
  await delay();
  const store = readStore();
  return clone(sectionData(store, section, qs, r)) as T;
}

function delay(ms = 80): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function recalcTicketCounts(store: DemoStore): void {
  const tickets = store.tickets.tickets;
  store.tickets.counts = {
    open: tickets.filter((t) => t.status === 'open').length,
    progress: tickets.filter((t) => t.status === 'progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
  };
}

function applyMutation(method: string, path: string, body: unknown, store: DemoStore): unknown {
  const actor = getAdminDemoActor();

  // Assignments
  if (method === 'POST' && path === '/admin/assignments') {
    const { title, instructionsHtml, course, courseSlug, dueDate } = body as {
      title: string;
      instructionsHtml: string;
      course: string;
      courseSlug: string;
      dueDate?: string | null;
    };
    const trimmedTitle = String(title ?? '').trim();
    if (!trimmedTitle) throw new Error('Assignment title is required');
    if (!String(course ?? '').trim()) throw new Error('Course is required');
    const html = String(instructionsHtml ?? '').trim();
    if (!html || html === '<p></p>' || html === '<br>') throw new Error('Assignment instructions are required');
    const plain = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const assignment: DemoAssignment = {
      id: `asg_${Date.now()}`,
      title: trimmedTitle,
      brief: plain.slice(0, 140),
      instructionsHtml: html,
      course: String(course).trim(),
      courseSlug: String(courseSlug ?? '').trim(),
      dueDate: dueDate || null,
      submissions: 0,
      reviewed: 0,
      createdAt: new Date().toISOString(),
    };
    store.assignments.assignments.unshift(assignment);
    addAudit(store, 'assignment.create', { id: assignment.id, title: trimmedTitle });
    return { ok: true, id: assignment.id };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/assignments\/[^/]+$/)) {
    const id = path.split('/')[3]!;
    const assignment = store.assignments.assignments.find((a) => a.id === id);
    if (!assignment) throw new Error('Assignment not found');
    const { title, instructionsHtml, course, courseSlug, dueDate } = body as {
      title?: string;
      instructionsHtml?: string;
      course?: string;
      courseSlug?: string;
      dueDate?: string | null;
    };
    if (title != null) {
      const trimmedTitle = String(title).trim();
      if (!trimmedTitle) throw new Error('Assignment title is required');
      assignment.title = trimmedTitle;
    }
    if (instructionsHtml != null) {
      const html = String(instructionsHtml).trim();
      if (!html || html === '<p></p>' || html === '<br>') throw new Error('Assignment instructions are required');
      assignment.instructionsHtml = html;
      assignment.brief = html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
    }
    if (course != null) assignment.course = String(course).trim();
    if (courseSlug != null) assignment.courseSlug = String(courseSlug).trim();
    if (dueDate !== undefined) assignment.dueDate = dueDate || null;
    addAudit(store, 'assignment.update', { id });
    return { ok: true };
  }

  // Coupons
  if (method === 'POST' && path === '/admin/coupons') {
    const { code, discountType, value, courseSlug, courseTitle, expiresAt, active, audience } = body as {
      code: string;
      discountType: 'percent' | 'flat';
      value: number;
      courseSlug?: string | null;
      courseTitle?: string | null;
      expiresAt?: string | null;
      active?: boolean;
      audience?: 'public' | 'affiliate';
    };
    const normalized = String(code ?? '')
      .trim()
      .toUpperCase();
    if (!normalized) throw new Error('Coupon code is required');
    if (store.coupons.coupons.some((c) => c.code === normalized)) throw new Error('Coupon code already exists');
    const type = discountType === 'flat' ? 'flat' : 'percent';
    const amount = Number(value) || 0;
    if (type === 'percent' && (amount < 1 || amount > 100)) throw new Error('Percent must be between 1 and 100');
    if (type === 'flat' && amount <= 0) throw new Error('Flat discount must be greater than 0');
    store.coupons.coupons.unshift({
      code: normalized,
      discountType: type,
      value: amount,
      courseSlug: courseSlug || null,
      courseTitle: courseTitle || null,
      audience: audience === 'affiliate' ? 'affiliate' : 'public',
      expiresAt: expiresAt || null,
      active: active ?? true,
      createdAt: new Date().toISOString(),
    });
    addAudit(store, 'coupon.create', { code: normalized, discountType: type, value: amount, audience });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/coupons\/[^/]+\/active$/)) {
    const code = decodeURIComponent(path.split('/')[3]!);
    const { active } = body as { active: boolean };
    const coupon = store.coupons.coupons.find((c) => c.code === code);
    if (!coupon) throw new Error('Coupon not found');
    if (active) {
      const expired =
        !!coupon.expiresAt && new Date(`${coupon.expiresAt}T23:59:59`).getTime() < Date.now();
      if (expired) throw new Error('Expired coupon — edit and set a new expiry date first');
    }
    coupon.active = active;
    addAudit(store, active ? 'coupon.activate' : 'coupon.deactivate', { code });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/coupons\/[^/]+$/)) {
    const code = decodeURIComponent(path.split('/')[3]!);
    const coupon = store.coupons.coupons.find((c) => c.code === code);
    if (!coupon) throw new Error('Coupon not found');
    const { discountType, value, courseSlug, courseTitle, expiresAt, active, audience } = body as {
      discountType?: 'percent' | 'flat';
      value?: number;
      courseSlug?: string | null;
      courseTitle?: string | null;
      expiresAt?: string | null;
      active?: boolean;
      audience?: 'public' | 'affiliate';
    };
    if (discountType) coupon.discountType = discountType === 'flat' ? 'flat' : 'percent';
    if (value != null) {
      const amount = Number(value) || 0;
      if (coupon.discountType === 'percent' && (amount < 1 || amount > 100)) {
        throw new Error('Percent must be between 1 and 100');
      }
      if (coupon.discountType === 'flat' && amount <= 0) throw new Error('Flat discount must be greater than 0');
      coupon.value = amount;
    }
    if (courseSlug !== undefined) coupon.courseSlug = courseSlug || null;
    if (courseTitle !== undefined) coupon.courseTitle = courseTitle || null;
    if (audience !== undefined) coupon.audience = audience === 'affiliate' ? 'affiliate' : 'public';
    if (expiresAt !== undefined) coupon.expiresAt = expiresAt || null;
    if (active !== undefined) coupon.active = active;
    const stillExpired =
      !!coupon.expiresAt && new Date(`${coupon.expiresAt}T23:59:59`).getTime() < Date.now();
    if (coupon.active && stillExpired) {
      throw new Error('Set a future expiry date to reactivate this coupon');
    }
    addAudit(store, 'coupon.update', { code });
    return { ok: true };
  }
  if (method === 'DELETE' && path.startsWith('/admin/coupons/')) {
    const code = decodeURIComponent(path.replace('/admin/coupons/', ''));
    store.coupons.coupons = store.coupons.coupons.filter((c) => c.code !== code);
    addAudit(store, 'coupon.delete', { code });
    return { ok: true };
  }

  // Courses
  if (method === 'PATCH' && path.match(/^\/admin\/courses\/[^/]+\/hidden$/)) {
    const slug = path.split('/')[3]!;
    const { hidden } = body as { hidden: boolean };
    const course = store.courses.courses.find((c) => c.slug === slug);
    if (course) course.hidden = hidden;
    addAudit(store, hidden ? 'course.unpublish' : 'course.publish', { slug });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/courses\/[^/]+\/live$/)) {
    const slug = path.split('/')[3]!;
    const { nextLiveAt } = body as { nextLiveAt: string | null };
    const course = store.courses.courses.find((c) => c.slug === slug);
    if (course) {
      course.nextLiveAt = nextLiveAt;
      if (nextLiveAt) course.isLive = true;
    }
    const detail = store.courseDetails[slug];
    if (detail) detail.nextLiveAt = nextLiveAt;
    addAudit(store, 'course.live', { slug, nextLiveAt });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/courses\/[^/]+\/price$/)) {
    const slug = path.split('/')[3]!;
    const { price } = body as { price: number };
    const course = store.courses.courses.find((c) => c.slug === slug);
    if (course) course.price = price;
    addAudit(store, 'course.price', { slug, price });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/courses\/[^/]+$/)) {
    const slug = path.split('/')[3]!;
    store.courses.courses = store.courses.courses.filter((c) => c.slug !== slug);
    delete store.courseDetails[slug];
    delete store.qbankQuestions[slug];
    store.reports.courses = store.reports.courses.filter((c) => c.slug !== slug);
    addAudit(store, 'course.delete', { slug });
    return { ok: true };
  }
  if (method === 'POST' && path === '/admin/courses/full') {
    const role = getAdminDemoRole();
    if (role === 'admin' || role === 'superadmin') {
      throw new Error('Admins cannot create courses. Instructors add courses from their dashboard.');
    }
    const payload = body as Record<string, unknown>;
    const slug = String(payload.slug ?? slugify(String(payload.title ?? 'course')));
    const title = String(payload.title ?? 'Untitled');
    const categorySlug = String(payload.categorySlug ?? 'general');
    const category =
      (payload.categoryName as string | undefined) ??
      categorySlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const price = Number(payload.price) || 0;
    const isLive = Boolean(payload.isLive);
    const hidden = payload.hidden !== false;
    const fakeStudents = Number(payload.fakeStudents) || 0;
    const modules = (payload.modules as { title: string; order: number; lessons: unknown[] }[]) ?? [];
    const totalLessons = modules.reduce((n, m) => n + (m.lessons?.length ?? 0), 0);
    const hours = Number(payload.hours) || 1;
    const profile = resolveInstructorProfile(store);
    const instructorId = profile?.id ?? String(payload.instructorId ?? payload.instructorSlug ?? `ins_${slug}`);
    const instructorName = profile?.name ?? String(payload.instructorName ?? 'Instructor');

    store.courses.courses.push({
      slug,
      title,
      category,
      price,
      realStudents: 0,
      instructorId,
      instructorName,
      hidden,
      isLive,
      nextLiveAt: payload.nextLiveAt ? String(payload.nextLiveAt) : null,
    });
    if (profile && profile.courseAccess === 'own' && !profile.courseSlugs.includes(slug)) {
      profile.courseSlugs.push(slug);
      profile.courseTitles.push(title);
    }

    const outcomes = (payload.outcomes as { name: string; role: string; outcome: string; hasVideo: boolean; videoId?: string }[]) ?? [];
    const leadMagnet = (payload.leadMagnet as { enabled: boolean; title?: string; description?: string; pdfUrl?: string }) ?? {
      enabled: false,
    };
    const featuredReviews =
      (payload.featuredReviews as { name: string; rating: number; body: string }[]) ?? [];

    store.courseDetails[slug] = {
      slug,
      title,
      category,
      categorySlug,
      level: String(payload.level ?? 'Beginner'),
      description: String(payload.description ?? ''),
      price,
      oldPrice: payload.oldPrice != null ? Number(payload.oldPrice) : null,
      discountPct:
        payload.oldPrice && Number(payload.oldPrice) > price
          ? Math.round((1 - price / Number(payload.oldPrice)) * 100)
          : null,
      isFree: Boolean(payload.isFree),
      isLive,
      isNew: true,
      rating: 0,
      ratingCount: 0,
      reviewCount: featuredReviews.length,
      students: fakeStudents,
      hours,
      icon: null,
      colorGradient: null,
      imageUrl: payload.imageUrl ? String(payload.imageUrl) : null,
      learnPoints: payload.learnPoints ?? [],
      instructor: {
        name: String(payload.instructorName ?? 'Instructor'),
        slug: String(payload.instructorSlug ?? 'instructor'),
        initials: String(payload.instructorName ?? 'IN')
          .split(' ')
          .map((w) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
        role: 'Instructor',
      },
      facts: {
        students: fakeStudents,
        hours,
        lessons: totalLessons,
        sheets: Number(payload.sheets) || 0,
        mocks: Number(payload.mocks) || 0,
        certificate: Boolean(payload.certificate),
        community: payload.community ? String(payload.community) : null,
        validity: payload.validity ? String(payload.validity) : 'Lifetime access',
      },
      gallery: payload.trailerVideoId
        ? [{ type: 'video', label: 'Course trailer', videoId: String(payload.trailerVideoId) }]
        : [],
      bonuses: payload.bonuses ?? [],
      faq: payload.faq ?? [],
      outcomes,
      leadMagnet,
      featuredReviews,
      modules,
      totalLessons,
      nextLiveAt: payload.nextLiveAt ? String(payload.nextLiveAt) : null,
      playlistUrl: payload.playlistUrl ?? null,
    };

    for (const r of featuredReviews) {
      store.reviews.reviews.push({
        id: `rev_${slug}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: r.name,
        email: '',
        rating: r.rating,
        body: r.body,
        course: title,
        courseSlug: slug,
        status: 'published',
        createdAt: new Date().toISOString(),
      });
    }

    store.reports.courses.push({
      slug,
      title,
      category,
      enrolled: 0,
      completed: 0,
      completionRate: 0,
      ratingAvg: null,
      reviewCount: 0,
      revenue: 0,
    });

    addAudit(store, 'course.create.full', { slug, title, lessons: totalLessons });
    return { ok: true, slug };
  }

  if (method === 'POST' && path === '/admin/courses') {
    const role = getAdminDemoRole();
    if (role === 'admin' || role === 'superadmin') {
      throw new Error('Admins cannot create courses. Instructors add courses from their dashboard.');
    }
    const { title, categorySlug, price, isLive } = body as {
      title: string;
      categorySlug?: string;
      price: number;
      isLive: boolean;
    };
    const slug = slugify(title) || `course-${Date.now()}`;
    const profile = resolveInstructorProfile(store);
    store.courses.courses.push({
      slug,
      title,
      category: categorySlug ?? 'General',
      price: price || 0,
      realStudents: 0,
      instructorId: profile?.id ?? 'ins_unassigned',
      instructorName: profile?.name ?? 'Unassigned',
      hidden: false,
      isLive: !!isLive,
      nextLiveAt: null,
    });
    if (profile && profile.courseAccess === 'own' && !profile.courseSlugs.includes(slug)) {
      profile.courseSlugs.push(slug);
      profile.courseTitles.push(title);
    }
    addAudit(store, 'course.create', { slug, title });
    return { ok: true };
  }

  // Staff invite
  if (method === 'POST' && path === '/admin/staff/invite') {
    const { name, email, role } = body as { name: string; email: string; role: string };
    const tempPassword = randomPassword();
    store.staff.staff.push({
      id: `st_${Date.now()}`,
      name,
      email,
      role,
    });
    addAudit(store, 'staff.invite', { email, role });
    return { tempPassword };
  }

  // Bundles
  if (method === 'POST' && path === '/admin/bundles') {
    const { title, slug, blurb, icon, discountPct, courseSlugs } = body as {
      title: string;
      slug?: string;
      blurb?: string;
      icon?: string | null;
      discountPct?: number;
      courseSlugs: string[];
    };
    const bundleSlug = String(slug ?? '').trim() || slugify(String(title ?? 'bundle'));
    const slugs = Array.isArray(courseSlugs) ? courseSlugs : [];
    const selected = store.courses.courses.filter((c) => slugs.includes(c.slug));
    if (!String(title ?? '').trim()) throw new Error('Bundle title is required');
    if (!selected.length) throw new Error('Select at least one course');
    if (store.bundles.bundles.some((b) => b.slug === bundleSlug)) throw new Error('Bundle slug already exists');

    const full = selected.reduce((sum, c) => sum + c.price, 0);
    const pct = Math.max(0, Math.min(80, Number(discountPct) || 0));
    const now = Math.round(full * (1 - pct / 100));

    store.bundles.bundles.push({
      slug: bundleSlug,
      title: String(title).trim(),
      icon: icon ? String(icon) : null,
      blurb: String(blurb ?? '').trim(),
      discountPct: pct,
      full,
      now,
      courses: selected.map((c) => c.title),
    });
    addAudit(store, 'bundle.create', { slug: bundleSlug, title: String(title).trim(), courses: slugs });
    return { ok: true, slug: bundleSlug };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/bundles\/[^/]+\/discount$/)) {
    const slug = path.split('/')[3]!;
    const { discountPct } = body as { discountPct: number };
    const bundle = store.bundles.bundles.find((b) => b.slug === slug);
    if (bundle) {
      const pct = Math.max(0, Math.min(80, Number(discountPct) || 0));
      bundle.discountPct = pct;
      bundle.now = Math.round(bundle.full * (1 - pct / 100));
    }
    addAudit(store, 'bundle.discount', { slug, discountPct });
    return { ok: true };
  }

  // Qbank
  if (method === 'POST' && path === '/admin/qbank') {
    const { courseSlug, moduleId, prompt, options, answerIndex, explanation } = body as {
      courseSlug: string;
      moduleId: string;
      prompt: string;
      options: string[];
      answerIndex: number;
      explanation?: string;
    };
    const modules = getCourseModules(store, courseSlug);
    if (!modules.some((m) => m.id === moduleId)) throw new Error('Select a valid module');
    const qid = `qb_${courseSlug}_${moduleId}_${Date.now()}`;
    if (!store.qbankQuestions[courseSlug]) store.qbankQuestions[courseSlug] = {};
    const list = store.qbankQuestions[courseSlug]![moduleId] ?? [];
    list.push({
      id: qid,
      prompt,
      options,
      answerIndex,
      explanation: explanation ?? '',
      isPlaceholder: false,
    });
    store.qbankQuestions[courseSlug]![moduleId] = list;
    addAudit(store, 'qbank.create', { courseSlug, moduleId, id: qid });
    return { ok: true };
  }
  if (method === 'DELETE' && path.startsWith('/admin/qbank/')) {
    const qid = path.replace('/admin/qbank/', '');
    for (const slug of Object.keys(store.qbankQuestions)) {
      for (const moduleId of Object.keys(store.qbankQuestions[slug]!)) {
        store.qbankQuestions[slug]![moduleId] = store.qbankQuestions[slug]![moduleId]!.filter((q) => q.id !== qid);
      }
    }
    addAudit(store, 'qbank.delete', { id: qid });
    return { ok: true };
  }

  // Enrollments
  if (method === 'POST' && path === '/admin/enrollments/grant') {
    const { userId, courseSlug } = body as { userId: string; courseSlug: string };
    const student = store.enrollments.students.find((s) => s.id === userId);
    const course = store.enrollments.courses.find((c) => c.slug === courseSlug);
    if (student && course) {
      store.enrollments.enrollments.push({
        userId,
        courseSlug,
        name: student.name,
        email: student.email,
        course: course.title,
        purchased: false,
        progressPct: 0,
        certId: null,
      });
    }
    addAudit(store, 'enrollment.grant', { userId, courseSlug });
    return { ok: true };
  }
  if (method === 'POST' && path === '/admin/enrollments/revoke') {
    const { userId, courseSlug } = body as { userId: string; courseSlug: string };
    store.enrollments.enrollments = store.enrollments.enrollments.filter(
      (e) => !(e.userId === userId && e.courseSlug === courseSlug),
    );
    addAudit(store, 'enrollment.revoke', { userId, courseSlug });
    return { ok: true };
  }

  // Users — quick instructor account (Users page); defaults to all-course access
  if (method === 'POST' && path === '/admin/users/instructors') {
    const { name, email, password } = body as { name: string; email: string; password: string };
    const trimmedName = String(name ?? '').trim();
    const trimmedEmail = String(email ?? '').trim().toLowerCase();
    if (!trimmedName) throw new Error('Name is required');
    if (!trimmedEmail) throw new Error('Email is required');
    if (!password || String(password).length < 6) throw new Error('Password must be at least 6 characters');
    if (store.users.users.some((u) => u.email.toLowerCase() === trimmedEmail)) {
      throw new Error('Email already in use');
    }
    const id = `u_${Date.now()}`;
    store.users.users.push({
      id,
      name: trimmedName,
      email: trimmedEmail,
      role: 'instructor',
      suspended: false,
      points: 0,
      joinedAt: new Date().toISOString(),
    });
    const now = new Date().toISOString();
    store.instructors.instructors.unshift({
      id: `ins_${Date.now()}`,
      name: trimmedName,
      email: trimmedEmail,
      source: 'admin',
      status: 'active',
      courseAccess: 'all',
      courseSlugs: [],
      courseTitles: [],
      topic: null,
      suspended: false,
      createdAt: now,
      approvedAt: now,
      paymentMethods: emptyPaymentMethods(),
    });
    addAudit(store, 'user.instructor.create', { id, email: trimmedEmail });
    return { ok: true, id };
  }

  // Instructors page — add with course access
  if (method === 'POST' && path === '/admin/instructors') {
    const { name, email, password, courseAccess, courseSlugs } = body as {
      name: string;
      email: string;
      password: string;
      courseAccess: 'all' | 'selected';
      courseSlugs?: string[];
    };
    const trimmedName = String(name ?? '').trim();
    const trimmedEmail = String(email ?? '').trim().toLowerCase();
    if (!trimmedName) throw new Error('Name is required');
    if (!trimmedEmail) throw new Error('Email is required');
    if (!password || String(password).length < 6) throw new Error('Password must be at least 6 characters');
    if (courseAccess !== 'all' && courseAccess !== 'selected') throw new Error('Invalid course access');
    const slugs = Array.isArray(courseSlugs) ? courseSlugs.map(String) : [];
    if (courseAccess === 'selected' && slugs.length === 0) {
      throw new Error('Select at least one course');
    }
    if (
      store.users.users.some((u) => u.email.toLowerCase() === trimmedEmail) ||
      store.instructors.instructors.some((i) => i.email.toLowerCase() === trimmedEmail)
    ) {
      throw new Error('Email already in use');
    }
    const courseMap = new Map(store.courses.courses.map((c) => [c.slug, c.title]));
    const titles = slugs.map((s) => courseMap.get(s) ?? s);
    const now = new Date().toISOString();
    const userId = `u_${Date.now()}`;
    store.users.users.push({
      id: userId,
      name: trimmedName,
      email: trimmedEmail,
      role: 'instructor',
      suspended: false,
      points: 0,
      joinedAt: now,
    });
    const instructor = {
      id: `ins_${Date.now()}`,
      name: trimmedName,
      email: trimmedEmail,
      source: 'admin' as const,
      status: 'active' as const,
      courseAccess,
      courseSlugs: courseAccess === 'selected' ? slugs : [],
      courseTitles: courseAccess === 'selected' ? titles : [],
      topic: null,
      suspended: false,
      createdAt: now,
      approvedAt: now,
      paymentMethods: emptyPaymentMethods(),
    };
    store.instructors.instructors.unshift(instructor);
    addAudit(store, 'instructor.create', { id: instructor.id, email: trimmedEmail, courseAccess });
    return { ok: true, id: instructor.id };
  }
  if (method === 'POST' && path.match(/^\/admin\/instructors\/[^/]+\/approve$/)) {
    const id = path.split('/')[3]!;
    const row = store.instructors.instructors.find((i) => i.id === id);
    if (!row) throw new Error('Instructor not found');
    if (row.status !== 'pending' && row.status !== 'rejected') {
      throw new Error('Only pending or rejected applications can be approved');
    }
    const now = new Date().toISOString();
    row.status = 'active';
    row.courseAccess = 'own';
    row.suspended = false;
    row.approvedAt = now;
    if (!row.paymentMethods) row.paymentMethods = emptyPaymentMethods();
    const tempPassword = randomPassword();
    if (!store.users.users.some((u) => u.email.toLowerCase() === row.email.toLowerCase())) {
      store.users.users.push({
        id: `u_${Date.now()}`,
        name: row.name,
        email: row.email,
        role: 'instructor',
        suspended: false,
        points: 0,
        joinedAt: now,
      });
    }
    addAudit(store, 'instructor.approve', { id });
    return { ok: true, tempPassword };
  }
  if (method === 'POST' && path.match(/^\/admin\/instructors\/[^/]+\/reject$/)) {
    const id = path.split('/')[3]!;
    const row = store.instructors.instructors.find((i) => i.id === id);
    if (!row) throw new Error('Instructor not found');
    if (row.status !== 'pending') throw new Error('Only pending applications can be rejected');
    row.status = 'rejected';
    addAudit(store, 'instructor.reject', { id });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/instructors\/[^/]+\/suspended$/)) {
    const id = path.split('/')[3]!;
    const { suspended } = body as { suspended: boolean };
    const row = store.instructors.instructors.find((i) => i.id === id);
    if (!row) throw new Error('Instructor not found');
    if (row.status !== 'active') throw new Error('Only active instructors can be suspended');
    row.suspended = Boolean(suspended);
    const user = store.users.users.find((u) => u.email.toLowerCase() === row.email.toLowerCase());
    if (user) user.suspended = row.suspended;
    addAudit(store, row.suspended ? 'instructor.suspend' : 'instructor.unsuspend', { id });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/instructors\/[^/]+$/)) {
    const id = path.split('/')[3]!;
    const row = store.instructors.instructors.find((i) => i.id === id);
    if (!row) throw new Error('Instructor not found');
    store.instructors.instructors = store.instructors.instructors.filter((i) => i.id !== id);
    store.users.users = store.users.users.filter((u) => u.email.toLowerCase() !== row.email.toLowerCase());
    addAudit(store, 'instructor.delete', { id, email: row.email });
    return { ok: true };
  }

  const ASSIGNABLE_USER_ROLES = new Set([
    'admin',
    'instructor',
    'affiliate',
    'student',
    'order_manager',
    'support',
  ]);

  if (method === 'PATCH' && path.match(/^\/admin\/users\/[^/]+\/role$/)) {
    const uid = path.split('/')[3]!;
    const { role } = body as { role: string };
    const actorRole = getAdminDemoRole();
    if (actorRole !== 'superadmin' && actorRole !== 'admin') forbidden();
    if (!ASSIGNABLE_USER_ROLES.has(role)) throw new Error('Invalid role');
    const user = store.users.users.find((u) => u.id === uid);
    if (user) user.role = role;
    addAudit(store, 'user.role', { userId: uid, role });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/students\/[^/]+\/suspended$/)) {
    const uid = path.split('/')[3]!;
    const { suspended } = body as { suspended: boolean };
    const user = store.users.users.find((u) => u.id === uid && u.role === 'student');
    if (!user) throw new Error('Student not found');
    user.suspended = suspended;
    addAudit(store, suspended ? 'student.suspend' : 'student.unsuspend', { userId: uid });
    return { ok: true };
  }
  if (method === 'POST' && path === '/admin/order-managers') {
    const { name, email, password } = body as { name: string; email: string; password: string };
    const trimmedName = String(name ?? '').trim();
    const trimmedEmail = String(email ?? '').trim().toLowerCase();
    if (!trimmedName) throw new Error('Name is required');
    if (!trimmedEmail) throw new Error('Email is required');
    if (String(password ?? '').length < 6) throw new Error('Password must be at least 6 characters');
    if (store.users.users.some((u) => u.email.toLowerCase() === trimmedEmail)) {
      throw new Error('Email already in use');
    }
    const id = `u_om_${Date.now()}`;
    store.users.users.push({
      id,
      name: trimmedName,
      email: trimmedEmail,
      role: 'order_manager',
      suspended: false,
      points: 0,
      joinedAt: new Date().toISOString(),
    });
    store.staff.staff.push({
      id: `st_om_${Date.now()}`,
      name: trimmedName,
      email: trimmedEmail,
      role: 'order_manager',
    });
    addAudit(store, 'ordermanager.create', { id, email: trimmedEmail });
    return { ok: true, id };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/order-managers\/[^/]+\/suspended$/)) {
    const uid = path.split('/')[3]!;
    const { suspended } = body as { suspended: boolean };
    const user = store.users.users.find((u) => u.id === uid && u.role === 'order_manager');
    if (!user) throw new Error('Order manager not found');
    user.suspended = Boolean(suspended);
    addAudit(store, suspended ? 'ordermanager.suspend' : 'ordermanager.unsuspend', { userId: uid });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/order-managers\/[^/]+$/)) {
    const uid = path.split('/')[3]!;
    const user = store.users.users.find((u) => u.id === uid && u.role === 'order_manager');
    if (!user) throw new Error('Order manager not found');
    store.users.users = store.users.users.filter((u) => u.id !== uid);
    store.staff.staff = store.staff.staff.filter((s) => s.email.toLowerCase() !== user.email.toLowerCase());
    addAudit(store, 'ordermanager.delete', { userId: uid });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/users\/[^/]+\/suspended$/)) {
    const uid = path.split('/')[3]!;
    const { suspended } = body as { suspended: boolean };
    const user = store.users.users.find((u) => u.id === uid);
    if (user) user.suspended = suspended;
    addAudit(store, suspended ? 'user.suspend' : 'user.unsuspend', { userId: uid });
    return { ok: true };
  }

  // Tickets & QA replies
  if (method === 'POST' && path.match(/^\/tickets\/[^/]+\/replies$/)) {
    const tid = path.split('/')[2]!;
    const { body: replyBody } = body as { body: string };
    const ticket = store.tickets.tickets.find((t) => t.id === tid);
    if (ticket) {
      ticket.replies.push({
        id: `tr_${Date.now()}`,
        body: replyBody,
        staff: true,
        user: { name: actor.email.split('@')[0] ?? 'Support' },
      });
      if (ticket.status === 'open') ticket.status = 'progress';
      recalcTicketCounts(store);
    }
    addAudit(store, 'ticket.reply', { ticketId: tid });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/tickets\/[^/]+\/status$/)) {
    const tid = path.split('/')[3]!;
    const { status } = body as { status: string };
    const ticket = store.tickets.tickets.find((t) => t.id === tid);
    if (ticket) ticket.status = status;
    recalcTicketCounts(store);
    addAudit(store, 'ticket.status', { ticketId: tid, status });
    return { ok: true };
  }
  if (method === 'POST' && path.match(/^\/qa\/[^/]+\/replies$/)) {
    const qid = path.split('/')[2]!;
    const { body: replyBody } = body as { body: string };
    const thread = store.qa.threads.find((t) => t.id === qid);
    if (thread) {
      thread.replies.push({
        id: `qr_${Date.now()}`,
        body: replyBody,
        staff: true,
        user: { name: 'Staff' },
      });
    }
    addAudit(store, 'qa.reply', { threadId: qid });
    return { ok: true };
  }

  // Settings
  if (method === 'PUT' && path === '/admin/settings') {
    const payload = body as {
      regOpen?: boolean;
      maintenance?: boolean;
      commissionPct?: number;
      affiliateCommissionPct?: number;
      supportEmail?: string;
    };
    const clampPct = (n: unknown, fallback: number) => {
      const v = Number(n);
      if (!Number.isFinite(v)) return fallback;
      return Math.min(100, Math.max(0, Math.round(v)));
    };
    store.settings.settings.regOpen = payload.regOpen ?? store.settings.settings.regOpen;
    store.settings.settings.maintenance = payload.maintenance ?? store.settings.settings.maintenance;
    store.settings.settings.commissionPct = clampPct(
      payload.commissionPct,
      store.settings.settings.commissionPct,
    );
    store.settings.settings.affiliateCommissionPct = clampPct(
      payload.affiliateCommissionPct,
      store.settings.settings.affiliateCommissionPct ?? 12,
    );
    if (payload.supportEmail != null) {
      store.settings.settings.supportEmail = String(payload.supportEmail).trim();
    }
    addAudit(store, 'settings.update', {
      commissionPct: store.settings.settings.commissionPct,
      affiliateCommissionPct: store.settings.settings.affiliateCommissionPct,
    });
    return { ok: true };
  }

  // Payouts
  if (method === 'POST' && path === '/admin/payouts/pay') {
    const { instructorId } = body as { instructorId: string; amount?: number };
    const payout = store.payouts.payouts.find((p) => p.instructorId === instructorId);
    if (!payout) throw new Error('Payout not found');
    payout.status = 'paid';
    payout.paidAt = new Date().toISOString();
    payout.reference = payout.reference || `PAY-${Date.now()}`;
    addAudit(store, 'payout.paid', { instructorId, amount: payout.instructorShare });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/payouts\/[^/]+\/status$/)) {
    const instructorId = path.split('/')[3]!;
    const { status } = body as { status: string };
    const payout = store.payouts.payouts.find((p) => p.instructorId === instructorId);
    if (!payout) throw new Error('Payout not found');
    if (!['pending', 'processing', 'paid'].includes(status)) throw new Error('Invalid payout status');
    payout.status = status as DemoPayout['status'];
    if (status === 'paid') {
      payout.paidAt = payout.paidAt || new Date().toISOString();
      payout.reference = payout.reference || `PAY-${Date.now()}`;
    } else {
      payout.paidAt = undefined;
      payout.reference = undefined;
    }
    addAudit(store, 'payout.status', { instructorId, status });
    return { ok: true };
  }

  // Book orders
  if (method === 'PATCH' && path.match(/^\/admin\/book-orders\/[^/]+\/status$/)) {
    const orderId = path.split('/')[3]!;
    const { status } = body as { status: string };
    const order = store.bookorders.orders.find((o) => o.orderId === orderId);
    if (!order) throw new Error('Book order not found');
    order.status = status as DemoBookOrder['status'];
    const now = new Date().toISOString();
    if (status === 'shipped') order.shippedAt = now;
    if (status === 'delivered') order.deliveredAt = now;
    recalcBookOrderCounts(store.bookorders);
    addAudit(store, 'bookorder.status', { orderId, status });
    return { ok: true };
  }

  // Books
  if (method === 'POST' && path === '/admin/books') {
    const { title, slug, author, category, pages, price, oldPrice, stock } = body as {
      title: string;
      slug?: string;
      author?: string;
      category: string;
      pages: number;
      price: number;
      oldPrice?: number | null;
      stock: number;
    };
    const trimmedTitle = String(title ?? '').trim();
    if (!trimmedTitle) throw new Error('Book title is required');
    const bookSlug = String(slug ?? '').trim() || slugify(trimmedTitle);
    if (store.books.books.some((b) => b.slug === bookSlug)) throw new Error('Book slug already exists');
    const stockNum = Math.max(0, Number(stock) || 0);
    const priceNum = Math.max(0, Number(price) || 0);
    const oldPriceNum = oldPrice == null ? null : Math.max(0, Number(oldPrice) || 0);
    store.books.books.unshift({
      slug: bookSlug,
      title: trimmedTitle,
      author: String(author ?? 'MAKTech Academy').trim() || 'MAKTech Academy',
      category: String(category ?? '').trim() || 'General',
      pages: Math.max(1, Number(pages) || 1),
      price: priceNum,
      oldPrice: oldPriceNum,
      stock: stockNum,
      inStock: stockNum > 0,
      createdAt: new Date().toISOString(),
    });
    addAudit(store, 'book.create', { slug: bookSlug, title: trimmedTitle });
    return { ok: true, slug: bookSlug };
  }

  // Orders — refund review
  if (method === 'POST' && path.match(/^\/admin\/orders\/[^/]+\/refund$/)) {
    const orderId = path.split('/')[3]!;
    const order = store.orders.orders.find((o) => o.orderId === orderId);
    if (!order) throw new Error('Order not found');
    if (order.status !== 'refund_requested') throw new Error('No pending refund request');
    order.status = 'refunded';
    order.refundedAt = new Date().toISOString();
    addAudit(store, 'order.refund', { orderId });
    return { ok: true };
  }
  if (method === 'POST' && path.match(/^\/admin\/orders\/[^/]+\/refund\/deny$/)) {
    const orderId = path.split('/')[3]!;
    const order = store.orders.orders.find((o) => o.orderId === orderId);
    if (!order) throw new Error('Order not found');
    if (order.status !== 'refund_requested') throw new Error('No pending refund request');
    order.status = 'paid';
    order.refundDeniedAt = new Date().toISOString();
    order.refundRequest = null;
    addAudit(store, 'order.refund.deny', { orderId });
    return { ok: true };
  }

  // Announce
  if (method === 'POST' && path === '/admin/announce') {
    const { title, body: annBody } = body as { title: string; body: string };
    const trimmedTitle = String(title ?? '').trim();
    const trimmedBody = String(annBody ?? '').trim();
    if (!trimmedTitle) throw new Error('Subject is required');
    if (!trimmedBody) throw new Error('Message is required');
    store.announce.announcements.unshift({
      id: `ann_${Date.now()}`,
      title: trimmedTitle,
      body: trimmedBody,
      createdAt: new Date().toISOString(),
    });
    addAudit(store, 'announce.send', { title: trimmedTitle });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/announce\/[^/]+$/)) {
    const id = path.split('/')[3]!;
    const before = store.announce.announcements.length;
    store.announce.announcements = store.announce.announcements.filter((a) => a.id !== id);
    if (store.announce.announcements.length === before) throw new Error('Announcement not found');
    addAudit(store, 'announce.delete', { id });
    return { ok: true };
  }
  if (method === 'POST' && path === '/admin/announce/promotions') {
    const { text, active = true } = body as { text: string; active?: boolean };
    const trimmed = String(text ?? '').trim();
    if (!trimmed) throw new Error('Promotion text is required');
    const id = `promo_${Date.now()}`;
    const makeActive = active !== false;
    if (makeActive) {
      for (const p of store.announce.promotions) p.active = false;
    }
    store.announce.promotions.unshift({
      id,
      text: trimmed,
      active: makeActive,
      createdAt: new Date().toISOString(),
    });
    if (makeActive) {
      store.announce.banner = { id, text: trimmed, active: true };
    }
    addAudit(store, 'promotion.create', { id, active: makeActive });
    return { ok: true, id };
  }
  if (method === 'PUT' && path.match(/^\/admin\/announce\/promotions\/[^/]+\/activate$/)) {
    const id = path.split('/')[4]!;
    const promo = store.announce.promotions.find((p) => p.id === id);
    if (!promo) throw new Error('Promotion not found');
    for (const p of store.announce.promotions) p.active = p.id === id;
    promo.active = true;
    store.announce.banner = { id: promo.id, text: promo.text, active: true };
    addAudit(store, 'promotion.activate', { id });
    return { ok: true };
  }
  if (method === 'PUT' && path.match(/^\/admin\/announce\/promotions\/[^/]+\/hide$/)) {
    const id = path.split('/')[4]!;
    const promo = store.announce.promotions.find((p) => p.id === id);
    if (!promo) throw new Error('Promotion not found');
    promo.active = false;
    if (store.announce.banner?.id === id) {
      store.announce.banner = { ...store.announce.banner, active: false };
    }
    addAudit(store, 'promotion.hide', { id });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/announce\/promotions\/[^/]+$/)) {
    const id = path.split('/')[4]!;
    const before = store.announce.promotions.length;
    store.announce.promotions = store.announce.promotions.filter((p) => p.id !== id);
    if (store.announce.promotions.length === before) throw new Error('Promotion not found');
    if (store.announce.banner?.id === id) store.announce.banner = null;
    addAudit(store, 'promotion.delete', { id });
    return { ok: true };
  }
  if (method === 'PUT' && path === '/admin/announce/banner') {
    const { text, active } = body as { text: string; active: boolean };
    const trimmed = String(text ?? '').trim();
    const id = active ? `promo_${Date.now()}` : (store.announce.banner?.id ?? `promo_${Date.now()}`);
    if (active && trimmed) {
      for (const p of store.announce.promotions) p.active = false;
      store.announce.promotions.unshift({
        id,
        text: trimmed,
        active: true,
        createdAt: new Date().toISOString(),
      });
      store.announce.banner = { id, text: trimmed, active: true };
    } else if (store.announce.banner) {
      store.announce.banner = { ...store.announce.banner, text: trimmed || store.announce.banner.text, active: false };
      const match = store.announce.promotions.find((p) => p.id === store.announce.banner?.id);
      if (match) match.active = false;
    }
    addAudit(store, active ? 'banner.publish' : 'banner.hide', { text: trimmed });
    return { ok: true };
  }

  // Reviews
  if (method === 'DELETE' && path.startsWith('/admin/reviews/')) {
    const rid = path.replace('/admin/reviews/', '');
    store.reviews.reviews = store.reviews.reviews.filter((r) => r.id !== rid);
    addAudit(store, 'review.delete', { id: rid });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/reviews\/[^/]+\/status$/)) {
    const rid = path.split('/')[3]!;
    const { status } = body as { status: string };
    const review = store.reviews.reviews.find((r) => r.id === rid);
    if (!review) throw new Error('Review not found');
    if (status !== 'published' && status !== 'hidden' && status !== 'flagged') {
      throw new Error('Invalid review status');
    }
    review.status = status;
    addAudit(store, 'review.status', { id: rid, status });
    return { ok: true };
  }

  // Free resources
  if (method === 'POST' && path === '/admin/free-resources') {
    const { title, description, category, fileName, pdfUrl } = body as {
      title: string;
      description?: string;
      category: string;
      fileName: string;
      pdfUrl: string;
    };
    const trimmedTitle = String(title ?? '').trim();
    if (!trimmedTitle) throw new Error('Title is required');
    if (!String(category ?? '').trim()) throw new Error('Category is required');
    if (!String(pdfUrl ?? '').trim()) throw new Error('PDF file is required');
    const now = new Date().toISOString();
    const resource = {
      id: `fr_${Date.now()}`,
      title: trimmedTitle,
      description: String(description ?? '').trim(),
      category: String(category).trim(),
      fileName: String(fileName ?? 'resource.pdf').trim() || 'resource.pdf',
      pdfUrl: String(pdfUrl),
      downloads: 0,
      createdAt: now,
      updatedAt: now,
    };
    store.freeresources.resources.unshift(resource);
    addAudit(store, 'freeresource.create', { id: resource.id, title: trimmedTitle });
    return { ok: true, id: resource.id };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/free-resources\/[^/]+$/)) {
    const id = path.split('/')[3]!;
    const resource = store.freeresources.resources.find((r) => r.id === id);
    if (!resource) throw new Error('Resource not found');
    const { title, description, category, fileName, pdfUrl } = body as {
      title?: string;
      description?: string;
      category?: string;
      fileName?: string;
      pdfUrl?: string;
    };
    if (title != null) {
      const trimmedTitle = String(title).trim();
      if (!trimmedTitle) throw new Error('Title is required');
      resource.title = trimmedTitle;
    }
    if (description != null) resource.description = String(description).trim();
    if (category != null) {
      const cat = String(category).trim();
      if (!cat) throw new Error('Category is required');
      resource.category = cat;
    }
    if (fileName != null && String(fileName).trim()) resource.fileName = String(fileName).trim();
    if (pdfUrl != null && String(pdfUrl).trim()) resource.pdfUrl = String(pdfUrl);
    resource.updatedAt = new Date().toISOString();
    addAudit(store, 'freeresource.update', { id });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/free-resources\/[^/]+$/)) {
    const id = path.split('/')[3]!;
    const before = store.freeresources.resources.length;
    store.freeresources.resources = store.freeresources.resources.filter((r) => r.id !== id);
    if (store.freeresources.resources.length === before) throw new Error('Resource not found');
    addAudit(store, 'freeresource.delete', { id });
    return { ok: true };
  }

  // Business leads
  if (method === 'POST' && path.match(/^\/admin\/leads\/[^/]+\/contacted$/)) {
    const lid = path.split('/')[3]!;
    const lead = store.leads.leads.find((l) => l.id === lid);
    if (lead) lead.status = 'contacted';
    addAudit(store, 'lead.contacted', { id: lid });
    return { ok: true };
  }
  if (method === 'PATCH' && path.match(/^\/admin\/leads\/[^/]+\/status$/)) {
    const lid = path.split('/')[3]!;
    const { status } = body as { status: string };
    const lead = store.leads.leads.find((l) => l.id === lid);
    if (!lead) throw new Error('Lead not found');
    if (!['new', 'contacted', 'visited', 'closed'].includes(status)) {
      throw new Error('Invalid lead status');
    }
    lead.status = status as typeof lead.status;
    addAudit(store, 'lead.status', { id: lid, status });
    return { ok: true };
  }
  if (method === 'DELETE' && path.match(/^\/admin\/leads\/[^/]+$/)) {
    const lid = path.split('/')[3]!;
    const before = store.leads.leads.length;
    store.leads.leads = store.leads.leads.filter((l) => l.id !== lid);
    if (store.leads.leads.length === before) throw new Error('Lead not found');
    addAudit(store, 'lead.delete', { id: lid });
    return { ok: true };
  }
  if (method === 'POST' && path.match(/^\/admin\/counselling\/[^/]+\/called$/)) {
    const bid = path.split('/')[3]!;
    const booking = store.counselling.bookings.find((b) => b.id === bid);
    if (booking) booking.status = 'called';
    addAudit(store, 'counselling.called', { id: bid });
    return { ok: true };
  }
  if (method === 'POST' && path.match(/^\/admin\/affiliates\/withdrawals\/[^/]+\/pay$/)) {
    const wid = path.split('/')[4]!;
    const wd = store.affiliates.withdrawals.find((w) => w.id === wid);
    if (wd && wd.status !== 'paid') {
      wd.status = 'paid';
      store.affiliates.stats.pendingWithdrawals = store.affiliates.withdrawals.filter(
        (w) => w.status === 'pending',
      ).length;
      // Available was already reduced when the affiliate requested withdrawal.
    }
    addAudit(store, 'affiliate.withdrawal.pay', { id: wid });
    return { ok: true };
  }

  // Staff profile (instructor incl. admin-added, or affiliate)
  if (method === 'PUT' && path === '/admin/profile') {
    const { name, phone, bio, headline } = body as {
      name?: string;
      phone?: string | null;
      bio?: string | null;
      headline?: string | null;
    };
    const trimmedName = String(name ?? '').trim();
    if (!trimmedName) throw new Error('Name is required');

    const affiliate = resolveAffiliateProfile(store);
    if (affiliate) {
      affiliate.name = trimmedName;
      if (phone !== undefined) affiliate.phone = String(phone ?? '').trim() || affiliate.phone;
      if (bio !== undefined) affiliate.bio = String(bio ?? '').trim() || null;
      addAudit(store, 'profile.update', { affiliateId: affiliate.id });
      return {
        ok: true,
        profile: {
          name: affiliate.name,
          email: affiliate.email,
          phone: affiliate.phone,
          bio: affiliate.bio ?? '',
        },
      };
    }

    const instructor = resolveInstructorProfile(store);
    if (!instructor) forbidden();
    instructor.name = trimmedName;
    if (phone !== undefined) instructor.phone = String(phone ?? '').trim() || null;
    if (bio !== undefined) instructor.bio = String(bio ?? '').trim() || null;
    if (headline !== undefined) instructor.headline = String(headline ?? '').trim() || null;
    addAudit(store, 'profile.update', { instructorId: instructor.id, source: instructor.source });
    return {
      ok: true,
      profile: {
        name: instructor.name,
        email: instructor.email,
        phone: instructor.phone ?? '',
        bio: instructor.bio ?? '',
        headline: instructor.headline ?? '',
      },
    };
  }

  // Payment methods — instructor (application) or affiliate
  if (method === 'PUT' && path === '/admin/payment/methods') {
    const affiliate = resolveAffiliateProfile(store);
    if (affiliate) {
      const methods = body as Partial<InstructorPaymentMethods>;
      const current = normalizeAffiliatePaymentMethods(affiliate.paymentMethods);
      affiliate.paymentMethods = applyPaymentMethodsPatch(current, methods);
      addAudit(store, 'payment.methods.update', { affiliateId: affiliate.id });
      return { ok: true, paymentMethods: affiliate.paymentMethods };
    }
    const profile = resolveInstructorProfile(store);
    if (!profile || profile.source !== 'application') forbidden();
    const methods = body as Partial<InstructorPaymentMethods>;
    const current = profile.paymentMethods ?? emptyPaymentMethods();
    profile.paymentMethods = applyPaymentMethodsPatch(current, methods);
    addAudit(store, 'payment.methods.update', { instructorId: profile.id });
    return { ok: true, paymentMethods: profile.paymentMethods };
  }

  if (method === 'POST' && path === '/admin/payment/request') {
    const { amount, method: payMethod } = body as {
      amount: number;
      method: 'bank' | 'bkash' | 'nagad' | 'rocket';
    };
    const amountPoisha = Math.round(Number(amount) || 0);
    if (amountPoisha < 10000) throw new Error('Minimum withdrawal is ৳100');

    const affiliate = resolveAffiliateProfile(store);
    if (affiliate) {
      const methods = normalizeAffiliatePaymentMethods(affiliate.paymentMethods);
      if (amountPoisha > affiliate.available) throw new Error('Amount exceeds available balance');
      const { payoutAccount } = resolvePayoutAccount(methods, payMethod);
      affiliate.available -= amountPoisha;
      store.affiliates.withdrawals.unshift({
        id: `wd_${Date.now()}`,
        name: affiliate.name,
        email: affiliate.email,
        phone: affiliate.phone,
        code: affiliate.code,
        amount: amountPoisha,
        method: payMethod,
        account: payoutAccount,
        status: 'pending',
        requestedAt: new Date().toISOString(),
      });
      store.affiliates.stats.pendingWithdrawals = store.affiliates.withdrawals.filter(
        (w) => w.status === 'pending',
      ).length;
      addAudit(store, 'payment.request', { affiliateId: affiliate.id, amount: amountPoisha, method: payMethod });
      return { ok: true, availableBalance: affiliate.available };
    }

    const profile = resolveInstructorProfile(store);
    if (!profile || profile.source !== 'application') forbidden();
    const methods = profile.paymentMethods ?? emptyPaymentMethods();
    const available = profile.availableBalance ?? 0;
    if (amountPoisha > available) throw new Error('Amount exceeds available balance');
    const { paymentMethod, payoutAccount } = resolvePayoutAccount(methods, payMethod);
    const now = new Date();
    const period = now.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    profile.availableBalance = available - amountPoisha;
    store.payouts.payouts.unshift({
      instructorId: profile.id,
      instructor: profile.name,
      email: profile.email,
      period: `Request · ${period}`,
      gross: amountPoisha,
      platform: 0,
      instructorShare: amountPoisha,
      status: 'pending',
      paymentMethod,
      payoutAccount,
      courses: [{ title: 'Withdrawal request', sales: amountPoisha, share: amountPoisha }],
    });
    addAudit(store, 'payment.request', { instructorId: profile.id, amount: amountPoisha, method: payMethod });
    return { ok: true, availableBalance: profile.availableBalance };
  }

  // Fallback — log and succeed so buttons are never dead in demo mode
  addAudit(store, `${method.toLowerCase()}.${path.replace(/^\//, '').replace(/\//g, '.')}`, body ?? null);
  return { ok: true };
}

export async function demoAdminRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  await delay();
  const store = readStore();
  const result = applyMutation(method.toUpperCase(), path, body, store);
  writeStore(store);
  return result as T;
}

/** Reset demo data (useful during development). */
export function resetAdminDemoStore(): void {
  if (!isBrowser()) return;
  localStorage.removeItem(STORAGE_KEY);
}

/** Public site-wide banner (active only). Used above the public header. */
export function demoGetPublicBanner(): { id: string; text: string } | null {
  const store = readStore();
  const b = store.announce.banner;
  if (b?.active && b.text.trim()) return { id: b.id, text: b.text.trim() };
  const live = (store.announce.promotions ?? []).find((p) => p.active && p.text.trim());
  if (live) return { id: live.id, text: live.text.trim() };
  return null;
}

/** Demo: count a referral link click for an affiliate code. */
export function recordDemoAffiliateClick(code: string): void {
  if (!isBrowser()) return;
  const store = readStore();
  const profile = findAffiliateByCode(store, code);
  if (!profile) return;
  profile.clicks += 1;
  writeStore(store);
}

/**
 * Demo: credit commission when a referred visitor purchases any course.
 * Attribution is affiliate-level (not locked to the landing course).
 * Writes one sales ledger row per course line.
 */
export function applyDemoAffiliatePurchase(
  code: string,
  orderTotalPoisha: number,
  items?: { slug: string; title: string; price: number }[],
  couponCode?: string | null,
): void {
  if (!isBrowser()) return;
  const store = readStore();
  const profile = findAffiliateByCode(store, code);
  if (!profile) return;
  const pct = store.settings.settings.affiliateCommissionPct ?? 12;
  const lines =
    items && items.length > 0
      ? items
      : [{ slug: 'order', title: 'Course order', price: Math.round(orderTotalPoisha) }];

  if (!Array.isArray(store.affiliates.sales)) store.affiliates.sales = [];

  let credited = 0;
  const now = new Date().toISOString();
  for (const item of lines) {
    const orderAmount = Math.max(0, Math.round(Number(item.price) || 0));
    const commission = Math.max(0, Math.round((orderAmount * pct) / 100));
    if (commission <= 0 && orderAmount <= 0) continue;
    credited += commission;
    store.affiliates.sales.unshift({
      id: `sale_${Date.now()}_${item.slug}`,
      affiliateId: profile.id,
      affiliateName: profile.name,
      affiliateCode: profile.code,
      courseSlug: item.slug,
      courseTitle: item.title,
      orderAmount,
      commission,
      commissionPct: pct,
      couponCode: couponCode ?? null,
      createdAt: now,
    });
  }
  if (credited <= 0) return;
  profile.signups += lines.length;
  profile.earned += credited;
  profile.available += credited;
  store.affiliates.stats.referredEnrolments += lines.length;
  store.affiliates.stats.commissionEarned += credited;
  addAudit(store, 'affiliate.purchase.credit', {
    code: profile.code,
    orderTotal: orderTotalPoisha,
    commission: credited,
    pct,
    courses: lines.map((l) => l.slug),
  });
  writeStore(store);
}

export interface DemoCouponCheckResult {
  valid: boolean;
  pct?: number;
  discountType?: 'percent' | 'flat';
  value?: number;
  audience?: 'public' | 'affiliate';
  message?: string;
}

/** Public/demo coupon validation — used by cart when USE_DEMO is on. */
export function demoCheckCoupon(
  code: string,
  opts?: { affiliateCode?: string | null; courseSlugs?: string[] },
): DemoCouponCheckResult {
  const store = readStore();
  const normalized = String(code ?? '')
    .trim()
    .toUpperCase();
  const coupon = store.coupons.coupons.find((c) => c.code === normalized);
  if (!coupon) return { valid: false, message: 'Sorry, that coupon code is not valid' };
  if (!coupon.active) return { valid: false, message: 'This coupon is no longer active' };
  if (coupon.expiresAt && new Date(`${coupon.expiresAt}T23:59:59`).getTime() < Date.now()) {
    return { valid: false, message: 'This coupon has expired' };
  }
  if (coupon.audience === 'affiliate') {
    const ref = String(opts?.affiliateCode ?? '')
      .trim()
      .toUpperCase();
    if (!ref || !findAffiliateByCode(store, ref)) {
      return {
        valid: false,
        audience: 'affiliate',
        message:
          'This is an affiliate promo. Open the affiliate referral link first, then apply this code.',
      };
    }
  }
  const slugs = opts?.courseSlugs ?? [];
  if (coupon.courseSlug && slugs.length > 0 && !slugs.includes(coupon.courseSlug)) {
    return {
      valid: false,
      message: `This coupon only applies to ${coupon.courseTitle ?? coupon.courseSlug}`,
    };
  }
  return {
    valid: true,
    pct: coupon.discountType === 'percent' ? coupon.value : undefined,
    discountType: coupon.discountType,
    value: coupon.value,
    audience: coupon.audience,
  };
}

export { ALL_ADMIN_SECTIONS };
